import {
  Box,
  Table as WSRTable,
  TableColumn as WSRTableColumn,
  TableProps,
} from '@wix/design-system';
import React, {
  ComponentType,
  ForwardRefExoticComponent,
  ReactElement,
} from 'react';
import { observer } from 'mobx-react-lite';
import { FiltersMap, KeyedItem } from '@wix/bex-core';
import { CollectionProvider } from '../CollectionContext';
import { InfiniteScrollTableState, ToolbarCollectionState } from '../../state';
import { CollectionToolbars } from '../CollectionToolbars';
import { BorderOnlyBox } from '../BorderOnlyBox';
import { ToolbarCollectionBaseProps } from '../ToolbarCollection';
import { CollectionTableContentBaseProps } from './CollectionTableContent';
import { classes, st } from '../common.st.css.js';

export interface CollectionTableWSRTableBaseProps<U, T, F extends FiltersMap>
  extends ToolbarCollectionBaseProps<T, F>,
    CollectionTableContentBaseProps,
    Omit<
      TableProps<KeyedItem<U>>,
      | 'columns'
      | 'data'
      | 'rowDataHook'
      | 'onRowClick'
      | 'onDragEnd'
      | 'onDragStart'
      | 'onDragCancel'
      | 'isDragAndDropDisabled'
      | 'hideBulkSelectionCheckbox'
    > {
  useShowLoaderWhenEmpty?: boolean;
  useNewInfiniteScrollLoader?: boolean;

  /**
   * Opt-in selection checkbox as sticky column by default
   * @external
   */
  useNewStickyColumns?: boolean;

  /**
   * Sets a limit on the number of selectable items, works with [`MultiBulkActionToolbar`](/pages/cairo/?path=/story/components-collection-features--multibulkactiontoolbar) feature
   * @external
   */
  maxSelection?: number;

  /**
   * Hides bulk selection checkbox in the table titlebar, If the [`MultiBulkActionToolbar`](/pages/cairo/?path=/story/components-collection-features--multibulkactiontoolbar) feature is enabled, this flag will have no effect.
   * @external
   */
  hideBulkSelectionCheckbox?: boolean;

  BodyElementType?: ComponentType<{ children: ReactElement[] }>;

  TrElementType?: ForwardRefExoticComponent<{
    children: ReactElement[];
    rowData: any;
    className: string;
  }>;
}

export interface CollectionTableWSRTableProps<U, T, F extends FiltersMap>
  extends CollectionTableWSRTableBaseProps<U, T, F> {
  state: {
    tableState: InfiniteScrollTableState<T, F>;
    toolbar: ToolbarCollectionState<T, F>;
    keyedItems: KeyedItem<U>[];
  };

  sticky: boolean;

  columns: WSRTableColumn<KeyedItem<U>>[];
  onRowClick?: (item: KeyedItem<U>, index: number) => void;
}

function _CollectionTableWSRTable<U, T, F extends FiltersMap = FiltersMap>(
  props: CollectionTableWSRTableProps<U, T, F>,
) {
  const {
    state,
    filters,
    title,
    subtitle,
    tabs,
    customColumns: customColumnsProp,
    multiLevelSorting: multiLevelSortingProp,
    views,
    extraToolbar,
    summaryBar,
    selectionToolbar,
    dragAndDropReorderModeToolbar,
    showSelection: showSelectionProp,
    exportButton,
    tags,
    showTotal,
    sticky,
    onRowClick,
    search,
    onSearchChange,
    onSearchClick,
    onSearchBlur,
    searchMaxLength,
    children,
    tableGridSwitchButton,
    useShowLoaderWhenEmpty,
    dragAndDrop,
    titleBarDisplay,
    horizontalScroll,
    stickyColumns,
    useNewStickyColumns,
    hideBulkSelectionCheckbox,
    columns,
    addNewButton,
    primaryActionButton,
    onSelectionChanged,
  } = props;

  const { toolbar, tableState: table, keyedItems } = state;
  const { bulk, collection: toolbarCollection, initTask } = toolbar;
  const { collection } = table;
  const {
    result: { hasAvailableItems },
  } = toolbarCollection;
  const { showRefreshLoader, showErrorState, showEmptyState } = collection;

  const Table = WSRTable as ComponentType<TableProps<KeyedItem<U>>>;

  const showSelection = showSelectionProp ?? selectionToolbar != null;

  const tableSkeletonEnabled = toolbar.container.internalExperiments?.enabled(
    'specs.cairo.TableSkeletonLoader',
  );
  const hideTitleBar =
    !hasAvailableItems || initTask.status.isLoading || initTask.status.isError;

  const showBorderBox =
    !(tableSkeletonEnabled && initTask.status.isLoading) &&
    (hideTitleBar || !titleBarDisplay);

  return (
    <div
      data-hook="collection-table"
      className={st(classes.fullHeight, classes.column)}
    >
      <Table
        {...props}
        withWrapper={false}
        data={keyedItems}
        columns={columns}
        onRowClick={onRowClick}
        // this forces WSR <Table/> to render the loader even when `data.length=0`
        showHeaderWhenEmpty={
          useShowLoaderWhenEmpty &&
          !showRefreshLoader &&
          !showErrorState &&
          !showEmptyState
        }
        hideBulkSelectionCheckbox={
          selectionToolbar ? false : hideBulkSelectionCheckbox
        }
        dragAndDrop={dragAndDrop}
        rowDataHook={(rowData) => `table-row-${rowData.key}`}
        controlled
        horizontalScroll={horizontalScroll}
        stickyColumns={table.getStickyColumnsCount({
          horizontalScroll,
          stickyColumns,
          showSelection,
          useNewStickyColumns,
        })}
        showSelection={showSelection}
        selectedIds={bulk.selectedIds}
        onSelectionStarted={state.toolbar.onSelectionStarted}
        onSelectionChanged={
          onSelectionChanged ??
          ((_selectedIds, _change) => {
            state.toolbar.onSelectionChanged(_selectedIds, _change);
          })
        }
      >
        <CollectionToolbars
          sticky={sticky}
          state={toolbar}
          search={search}
          customColumns={customColumnsProp}
          multiLevelSorting={multiLevelSortingProp}
          views={views}
          onSearchChange={onSearchChange}
          onSearchClick={onSearchClick}
          searchMaxLength={searchMaxLength}
          onSearchBlur={onSearchBlur}
          title={title}
          subtitle={subtitle}
          tabs={tabs}
          filters={filters}
          showTotal={showTotal}
          selectionToolbar={selectionToolbar}
          dragAndDropReorderModeToolbar={dragAndDropReorderModeToolbar}
          extraToolbar={extraToolbar}
          summaryBar={summaryBar}
          titleBar={
            <>
              {showBorderBox && <BorderOnlyBox />}
              {!hideTitleBar && (
                <div
                  data-hook="titlebar-a11y-wrapper"
                  style={{ display: titleBarDisplay ? undefined : 'none' }}
                >
                  <WSRTable.Titlebar />
                </div>
              )}
            </>
          }
          tableGridSwitchButton={tableGridSwitchButton}
          exportButton={exportButton}
          tags={tags}
          addNewButton={addNewButton}
          primaryActionButton={primaryActionButton}
        />
        <CollectionProvider value={collection}>
          <Box
            width="100%"
            direction="vertical"
            minHeight={0}
            flex={1}
            verticalAlign="middle"
          >
            {children}
          </Box>
        </CollectionProvider>
      </Table>
    </div>
  );
}

export const CollectionTableWSRTable = observer(_CollectionTableWSRTable);
